import { useCallback, useMemo } from "react";

import { graphql, useFragment, useMutation } from "react-relay";

import { getTaskCreateInput } from "./getTaskCreateInput";
import { getTaskUpdateInput } from "./getTaskUpdateInput";
import { Connection, Section, Task } from "../State/Routine/types";
import {
  AddConnection,
  UpdateRoutineCreateSectionInput,
  useSaveRoutineEditMutation,
} from "./__generated__/useSaveRoutineEditMutation.graphql";

import { useSaveRoutineEdit_routine$key } from "./__generated__/useSaveRoutineEdit_routine.graphql";
import { useAlert } from "../../../common/Alert";

import { useIntl } from "react-intl";
import { ControlType, useRoutineEditState } from "../State/RoutineEditState";
import { getControlType } from "../RoutineEditMain/RoutineEditControlTypeCard/getControlType";
import type {
  RoutineScheduleDaily,
  RoutineScheduleWeekly,
  RoutineScheduleMonthly,
} from "../RoutineEditMain/RoutineEditNavigation";
import { resolveLocalTaskImportance } from "./resolveLocalTaskImportance";

function useValidateVariables() {
  const intl = useIntl();
  return useCallback(
    (
      variables: useSaveRoutineEditMutation["variables"],
      remoteTitle: string
    ) => {
      const errors: { title: string }[] = [];
      if (
        variables.input.setTitle === "" ||
        (!variables.input.setTitle && remoteTitle === "")
      ) {
        errors.push({
          title: intl.formatMessage({
            defaultMessage: "Please specify a title before you save.",
            description: "validateRoutineEditVariables no title message",
          }),
        });
      }
      return errors;
    },
    [intl]
  );
}

const controlTypeCheckInitialState: {
  tasks: Task[];
  connections: Connection[];
  sections: Section[];
} = {
  tasks: [
    {
      id: "t1",
      images: [],
      instructions: "",
      title: "",
      type: "TaskCheck",
      sentiments: ["POSITIVE" as const],
    },
  ],
  connections: [
    {
      taskId: "t1",
      sectionId: "s1",
      ordinal: 0,
      value: 0,
      orgTopic: null,
    },
  ],
  sections: [
    {
      id: "s1",
      title: "",
      assignedTeam: null,
      ordinal: 0,
      orgCanBeDisabled: null,
      orgTopic: null,
    },
  ],
};

const getLocalTasks: (_: {
  controlType: ControlType;
  taskParams: Task[];
  remoteControlType: ControlType;
}) => Task[] | undefined = ({ controlType, taskParams, remoteControlType }) => {
  switch (controlType) {
    case "checklist":
      return taskParams ?? undefined;
    case "check":
      return remoteControlType === "check"
        ? undefined
        : controlTypeCheckInitialState.tasks;
    case "none":
      return [];
  }
};

function getLocalSections({
  controlType,
  sectionParams,
  remoteControlType,
}: {
  controlType: ControlType;
  sectionParams: Section[];
  remoteControlType: ControlType;
}) {
  switch (controlType) {
    case "checklist":
      return sectionParams ?? undefined;
    case "check":
      return remoteControlType === "check"
        ? undefined
        : controlTypeCheckInitialState.sections;
    case "none":
      return [];
  }
}

function getLocalConnections({
  controlType,
  connectionParams,
  remoteControlType,
}: {
  controlType: ControlType;
  connectionParams: Connection[];
  remoteControlType: ControlType;
}) {
  switch (controlType) {
    case "checklist":
      return connectionParams ?? undefined;
    case "check":
      return remoteControlType === "check"
        ? undefined
        : controlTypeCheckInitialState.connections;
    case "none":
      return [];
  }
}

function undefinedIfEmpty<T>(array: T[] | undefined) {
  return array?.length ? array : undefined;
}

// All localTasks that are not in remoteTaskIds
function getCreateTasks(
  localTasks: Task[] | undefined,
  remoteTaskIds: string[]
) {
  return undefinedIfEmpty(
    localTasks?.flatMap((_task) =>
      remoteTaskIds.includes(_task.id) ? [] : [getTaskCreateInput(_task)]
    )
  );
}
// All localTasks that are in remoteTaskIds
function getUpdateTasks(
  localTasks: Task[] | undefined,
  remoteTaskIds: string[]
) {
  return undefinedIfEmpty(
    localTasks?.flatMap((_task) =>
      !remoteTaskIds.includes(_task.id) ? [] : [getTaskUpdateInput(_task)]
    )
  );
}

function getCreateSections({
  localSections,
  remoteSectionIds,
}: {
  localSections: Section[] | undefined;
  remoteSectionIds: string[];
}): UpdateRoutineCreateSectionInput[] | undefined {
  return undefinedIfEmpty(
    localSections?.flatMap((section) =>
      remoteSectionIds.includes(section.id)
        ? []
        : [
            {
              id: section.id,
              title: section.title,
              teamId: section.assignedTeam?.id,
              orgTopicId: section.orgTopic?.id,
              canBeDisabled: section.orgCanBeDisabled,
            },
          ]
    )
  );
}

function getUpdateSections({
  localSections,
  localConnections,
  remoteSectionIds,
}: {
  localSections: Section[] | undefined;
  localConnections: Connection[] | undefined;
  remoteSectionIds: string[];
}) {
  return undefinedIfEmpty(
    localSections?.flatMap((section) =>
      remoteSectionIds.includes(section.id)
        ? [
            {
              sectionId: section.id,
              setTeamId: section.assignedTeam?.id,
              unsetTeamId: !section.assignedTeam,
              setTaskOrder: localConnections
                ?.filter(({ sectionId }) => section.id === sectionId)
                .sort((a, b) => a.ordinal - b.ordinal)
                .map(({ taskId }) => taskId),
              setTitle: section.title,
              setOrgTopicId: section.orgTopic?.id,
              unsetOrgTopicId: !section.orgTopic,
              setCanBeDisabled: section.orgCanBeDisabled,
            },
          ]
        : []
    )
  );
}

// Local connections formated as createConnections
function getCreateConnections(
  localConnections: Connection[] | undefined,
  localTasks: Task[] | undefined
): AddConnection[] | undefined {
  return undefinedIfEmpty(
    localConnections?.map((c): AddConnection => {
      const base = {
        sectionId: c.sectionId,
        taskId: c.taskId,
        topicId: c.orgTopic?.id,
        canBeDisabled: c.canBeDisabled,
      };
      const localTask = localTasks?.find((t) => t.id === c.taskId);
      const taskType = localTask?.type;
      return {
        check: taskType === "TaskCheck" ? base : undefined,
        number:
          taskType === "TaskNumber"
            ? {
                ...base,
                greaterThan: localTask?.goal === "min" ? c.value : undefined,
                lessThan: localTask?.goal === "max" ? c.value : undefined,
              }
            : undefined,
        text: taskType === "TaskText" ? base : undefined,
      };
    })
  );
}

// If hasLocalConnections return all remoteConnections formated as removeConnections
function getRemoveConnections(
  hasLocalConnections: boolean,
  remoteConnections: readonly { sectionId: string; taskId: string }[]
) {
  return undefinedIfEmpty(
    hasLocalConnections
      ? remoteConnections.map(({ sectionId, taskId }) => ({
          sectionId,
          taskId,
        }))
      : undefined
  );
}

// If localSections is defined return all remoteSectionIds that are not in localSections
function getDeleteSections({
  localSections,
  remoteSectionIds,
}: {
  localSections: { id: string }[] | undefined;
  remoteSectionIds: string[];
}) {
  return undefinedIfEmpty(
    localSections !== undefined
      ? remoteSectionIds.flatMap((sectionId) =>
          !localSections.find((s) => s.id === sectionId) ? [{ sectionId }] : []
        )
      : undefined
  );
}

function getSetSectionOrder(localSections: Section[] | undefined) {
  return undefinedIfEmpty(
    localSections
      ?.slice()
      .sort((a, b) => a.ordinal - b.ordinal)
      .map(({ id }) => id)
  );
}

function getUpdateScheduleDaily(
  schedule: RoutineScheduleDaily,
  tasks: string[]
) {
  return {
    isAllDay: schedule.isAllDay,
    weekdays: schedule.weekdays.map((w) => {
      return {
        weekday: w.weekday,
        startTime: schedule.isAllDay ? null : w.startTime,
        tasks: resolveLocalTaskImportance(
          tasks,
          w.originalTaskImportance,
          w.taskImportanceChanges
        ),
      };
    }),
    deadlineDuration: schedule.deadlineDuration,
  };
}

function getUpdateScheduleWeekly(schedule: RoutineScheduleWeekly) {
  return {
    startWeekday: schedule.startWeekday,
    endWeekday: schedule.endWeekday,
    weeks: schedule.weeks,
  };
}

function getUpdateScheduleMonthly(schedule: RoutineScheduleMonthly) {
  return {
    months: schedule.months,
    startDay: schedule.startDay,
    endDay: schedule.endDay,
  };
}

function getUpdateSchedule(
  localSchedule:
    | RoutineScheduleDaily
    | RoutineScheduleWeekly
    | RoutineScheduleMonthly
    | undefined,
  tasks: string[]
) {
  if (localSchedule) {
    return {
      daily:
        localSchedule.freq === "daily"
          ? getUpdateScheduleDaily(localSchedule, tasks)
          : undefined,
      weekly:
        localSchedule.freq === "weekly"
          ? getUpdateScheduleWeekly(localSchedule)
          : undefined,
      monthly:
        localSchedule.freq === "monthly"
          ? getUpdateScheduleMonthly(localSchedule)
          : undefined,
    };
  }
  return undefined;
}

export function useSaveRoutineEdit(props: {
  routine: useSaveRoutineEdit_routine$key;
  onCompleted(): void;
  setOrgTopicId?: string;
}) {
  const validateVariables = useValidateVariables();
  const Alert = useAlert();
  const { onCompleted } = props;
  const params = useRoutineEditState();
  const routine = useFragment(
    graphql`
      fragment useSaveRoutineEdit_routine on Routine {
        id
        title
        description

        descriptionFiles {
          id
        }
        descriptionVideos {
          id
        }
        ownerTeam {
          id
        }
        assignedRoles {
          role {
            id
          }
        }
        currentSchedule {
          __typename
          ... on RoutineScheduleDaily {
            hasAdvancedScheduling
          }
        }
        activeVersion {
          tasks {
            id
            title
            __typename
            ... on TaskCheck {
              sentiments
            }
          }
          sections {
            section {
              id
            }
          }
          connections {
            sectionId
            taskId
          }
        }
      }
    `,
    props.routine
  );

  const [save, loading] = useMutation<useSaveRoutineEditMutation>(graphql`
    mutation useSaveRoutineEditMutation($id: ID!, $input: UpdateRoutineInput!) {
      routine(id: $id) {
        update(input: $input) {
          routine {
            ...RoutineEditMainRoutine_Routine
          }
        }
      }
    }
  `);

  const remoteControlType = useMemo(
    () =>
      getControlType(
        routine.activeVersion.tasks.map((t) => ({
          id: t.id,
          title: t.title,
          sentiments: t.sentiments,
          type: t.__typename,
        }))
      ),
    [routine.activeVersion.tasks]
  );

  const remoteTaskIds = useMemo(
    () => routine.activeVersion.tasks.map((t) => t.id),
    [routine.activeVersion.tasks]
  );

  const remoteSectionIds = useMemo(
    () => routine.activeVersion.sections.map((s) => s.section.id),
    [routine.activeVersion.sections]
  );

  const onSave = useCallback(() => {
    const controlType = params.controlType ?? remoteControlType;
    const localTasks = getLocalTasks({
      controlType,
      taskParams: params.tasks ?? [],
      remoteControlType,
    });
    const taskIds =
      localTasks?.map((t) => t.id) ??
      routine.activeVersion.tasks.map((t) => t.id);
    const localSections = getLocalSections({
      controlType,
      sectionParams: params.sections ?? [],
      remoteControlType,
    });
    const localConnections = getLocalConnections({
      controlType,
      connectionParams: params.connections ?? [],
      remoteControlType,
    });
    const createTasks = getCreateTasks(localTasks, remoteTaskIds);
    const updateTasks = getUpdateTasks(localTasks, remoteTaskIds);
    const createConnections = getCreateConnections(
      localConnections,
      localTasks
    );
    const removeConnections = getRemoveConnections(
      localConnections !== undefined,
      routine.activeVersion.connections
    );
    const deleteSections = getDeleteSections({
      localSections,
      remoteSectionIds,
    });
    const createSections = getCreateSections({
      localSections,
      remoteSectionIds,
    });
    const updateSections = getUpdateSections({
      localSections,
      localConnections,
      remoteSectionIds,
    });

    const setSectionOrder = getSetSectionOrder(localSections);

    const localSchedule =
      params.scheduleFreq === "daily"
        ? params.scheduleDaily
        : params.scheduleFreq === "weekly"
          ? params.scheduleWeekly
          : params.scheduleFreq === "monthly"
            ? params.scheduleMonthly
            : undefined;

    const updateSchedule = getUpdateSchedule(
      localSchedule,
      routine.currentSchedule?.__typename === "RoutineScheduleDaily" &&
        routine.currentSchedule.hasAdvancedScheduling
        ? taskIds
        : []
    );

    const remoteFileIds = routine.descriptionFiles.flatMap((f) =>
      params.removeDescriptionFileIds.includes(f.id) ? [] : [f.id]
    );

    const addDescriptionFileIds = params.addDescriptionFiles.flatMap((file) =>
      remoteFileIds.findIndex((id) => id === file.id) === -1 ? [file.id] : []
    );

    const variables: useSaveRoutineEditMutation["variables"] = {
      id: routine.id,
      input: {
        // schedule
        updateSchedule,
        // checklist
        updateTasks,
        createTasks,
        removeConnections,
        createConnections,
        deleteSections,
        updateSections,
        createSections,
        setSectionOrder,
        // content
        setTitle: params.title !== routine.title ? params.title : undefined,
        setDescription:
          params.description !== routine.description
            ? params.description
            : undefined,
        addDescriptionFileIds:
          addDescriptionFileIds.length > 0 ? addDescriptionFileIds : undefined,
        removeDescriptionFileIds:
          params.removeDescriptionFileIds.length > 0
            ? params.removeDescriptionFileIds
            : undefined,

        addDescriptionImageIds: params.addDescriptionMedia
          .map((i) => i.id)
          .filter((i) => i !== undefined),
        removeDescriptionMediaIds:
          params.removeDescriptionMediaIds.length > 0
            ? params.removeDescriptionMediaIds
            : undefined,

        setTeamId:
          routine.ownerTeam && params.team?.id !== routine.ownerTeam.id
            ? params.team?.id
            : undefined,

        setOrgTopicId: props.setOrgTopicId,

        // assigned roles
        assignedRoleIds: (() => {
          if (params.assignedRoleIds === undefined) return undefined;
          const remoteRoleIds = routine.assignedRoles
            .map((ar) => ar.role?.id)
            .filter((id): id is string => !!id)
            .sort();
          const localRoleIds = [...params.assignedRoleIds].sort();
          if (
            remoteRoleIds.length === localRoleIds.length &&
            remoteRoleIds.every((id, i) => id === localRoleIds[i])
          ) {
            return undefined;
          }
          return params.assignedRoleIds;
        })(),
      },
    };

    const validationErrors = validateVariables(variables, routine.title);
    if (validationErrors.length > 0) {
      Alert.error(validationErrors[0].title);
    } else {
      save({
        variables,
        onCompleted,
        onError: (e) => {
          console.log(JSON.stringify(e, null, 2));
          Alert.error("Ett fel inträffade");
        },
      });
    }
  }, [
    Alert,
    onCompleted,
    params.addDescriptionFiles,
    params.addDescriptionMedia,
    params.assignedRoleIds,
    params.connections,
    params.controlType,
    params.description,
    params.removeDescriptionFileIds,
    params.removeDescriptionMediaIds,
    params.scheduleDaily,
    params.scheduleFreq,
    params.scheduleMonthly,
    params.scheduleWeekly,
    params.sections,
    params.tasks,
    params.team?.id,
    params.title,
    props.setOrgTopicId,
    remoteControlType,
    remoteSectionIds,
    remoteTaskIds,
    routine.activeVersion.connections,
    routine.activeVersion.tasks,
    routine.assignedRoles,
    routine.currentSchedule,
    routine.description,
    routine.descriptionFiles,
    routine.id,
    routine.ownerTeam,
    routine.title,
    save,
    validateVariables,
  ]);

  return [onSave, loading] as const;
}
